/**
 * Translation Gate Component
 * Chinese-to-English translation exercise — AI-evaluated
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationQuestion, GateEvaluationResult } from '../../types/writing';
import './GateComponents.css';

interface TranslationGateProps {
  question: TranslationQuestion;
  onSubmit: (answer: string) => void;
  submitting: boolean;
  result: GateEvaluationResult | null;
  onSaveToNotes?: () => void;
  onSaveItemToNotes?: (item: string) => void;
}

const TranslationGate: React.FC<TranslationGateProps> = ({
  question,
  onSubmit,
  submitting,
  result,
}) => {
  const { t } = useTranslation(['writing', 'common']);
  const [answer, setAnswer] = useState('');

  // Reset answer when question changes
  useEffect(() => {
    setAnswer('');
  }, [question.id]);

  const handleSubmit = () => {
    if (answer.trim() && !submitting) {
      onSubmit(answer.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="gate-question translation-gate">
      <div className={`gate-question__card ${result ? 'has-result' : ''}`}>
        <div className="gate-question__header">
          <span className="gate-question__type">{t('translateToEnglish')}</span>
        </div>

        <div className="gate-question__content">
          {/* Source text (Chinese) */}
          <p className="translation-source">{question.sourceText}</p>

          {/* Keywords hint */}
          {question.keywords && question.keywords.length > 0 && !result && (
            <div className="translation-keywords">
              <span className="translation-keywords__label">{t('keywords')}:</span>
              {question.keywords.map((kw, i) => (
                <span key={i} className="translation-keywords__tag">{kw}</span>
              ))}
            </div>
          )}

          {/* Hint */}
          {question.hint && !result && (
            <p className="translation-hint">{t('hint')}: {question.hint}</p>
          )}
        </div>

        {/* Result badge */}
        {result && (
          <div className={`result-stamp ${result.passed ? 'result-stamp--correct' : 'result-stamp--incorrect'}`}>
            {result.passed ? '✓' : '✗'}
          </div>
        )}

        {/* Text input */}
        {!result && (
          <div className="translation-input-container">
            <textarea
              className="translation-input"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('typeYourTranslation')}
              disabled={submitting}
              autoFocus
              rows={3}
            />
          </div>
        )}

        {/* Show result */}
        {result && (
          <div className="translation-result">
            <div className="translation-result__section">
              <span className="translation-result__label">{t('yourAnswer')}:</span>
              <p className={`translation-result__answer ${result.passed ? 'correct' : 'incorrect'}`}>
                {answer}
              </p>
            </div>

            {result.improvedVersion && (
              <div className="translation-result__section">
                <span className="translation-result__label">{t('referenceAnswer')}:</span>
                <p className="translation-result__reference">{result.improvedVersion}</p>
              </div>
            )}

            {result.feedback && (
              <div className="translation-result__section">
                <span className="translation-result__label">{t('feedback')}:</span>
                <p className="translation-result__feedback">{result.feedback}</p>
              </div>
            )}

            {result.grammarIssues && result.grammarIssues.length > 0 && (
              <div className="translation-result__section">
                <span className="translation-result__label">{t('grammarIssues')}:</span>
                <ul className="translation-result__issues">
                  {result.grammarIssues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Submit Button */}
      {!result && (
        <button
          className="gate-submit-btn"
          onClick={handleSubmit}
          disabled={!answer.trim() || submitting}
        >
          {submitting ? t('submitting') : t('submit')}
        </button>
      )}
    </div>
  );
};

export default TranslationGate;
